/**
 * Pantalla **histórico de un rango de talonario** (solo lectura). Sirve
 * `ROUTES.talonarioHistorical` (`/talonarios/historico/:worker/:start/:end`).
 * Equivale a `historical_talonario_orders.html` del SSR.
 *
 * Exige `manage_talonarios` (vía `RequireManageTalonarios`). Carga el grid de un
 * rango YA NO vigente (`useTalonarioRangeGrid`) y lo pinta en modo `readOnly` (sin
 * botones de marcar/desmarcar). Útil para auditar talonarios antiguos. Los
 * parámetros de rango se leen de la URL y se parsean a entero. Presentacional +
 * un hook de datos; sin `fetch`.
 */
import { useParams, Link } from "react-router-dom";

import { Button, Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { ROUTES } from "@/features/navigation";

import { talonarioErrorMessage } from "../format";
import { useTalonarioRangeGrid } from "../hooks/useTalonarioRangeGrid";
import { RequireManageTalonarios } from "./RequireManageTalonarios";
import { TalonarioGridSummary } from "./TalonarioGridSummary";
import { TalonarioGridView } from "./TalonarioGridView";

export function HistoricalTalonarioScreen() {
  const {
    worker = "",
    start = "",
    end = "",
  } = useParams<{ worker: string; start: string; end: string }>();
  const startNum = Number(start);
  const endNum = Number(end);
  const grid = useTalonarioRangeGrid(worker, startNum, endNum);

  return (
    <RequireManageTalonarios title="Talonario histórico">
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="inline-flex items-center rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-medium text-violet-700">
              Talonario histórico
            </span>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              {worker} · {start}–{end}
            </h1>
            <p className="text-sm text-slate-500">
              Este talonario ya no está en uso. Solo lectura.
            </p>
          </div>
          <Button asChild variant="ghost" size="md">
            <Link to={ROUTES.talonarioHistory}>Volver al historial</Link>
          </Button>
        </div>

        {grid.isPending ? (
          <p className="text-sm text-slate-500" role="status">
            Cargando talonario histórico…
          </p>
        ) : grid.isError ? (
          <p className="text-sm text-red-600" role="alert">
            {talonarioErrorMessage(grid.error)}
          </p>
        ) : (
          <>
            <TalonarioGridSummary grid={grid.data} />
            <Card>
              <CardHeader>
                <CardTitle>Rango {grid.data.range}</CardTitle>
              </CardHeader>
              <CardContent>
                <TalonarioGridView numbers={grid.data.numbers} readOnly />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </RequireManageTalonarios>
  );
}
